import { type UseFormReturn } from "react-hook-form";

import { MMM_PRETTY_STATES } from "@features/mmm/components/top-bars";
import { formatDate } from "@features/mmm/utils/formatters";
import { MMMState } from "@north-beam/nb-client-side";
import { type GetBasicMediaPlans_me_mmm_basicMediaPlansData_modelConfigurations } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { type MMMClientState } from "@north-beam/nb-common";
import { Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";

import { useIsAutoblessedModelsEnabled } from "../../hooks/use-is-autoblessed-models-enabled";
import { type MediaPlanTableInterface } from "../../media-plan-table/media-plan-table";
import {
  DEPRECATED_WIZARD_STEPS,
  WIZARD_STEPS_AUTOBLESSED_MODELS,
} from "../consts";
import { type MediaPlan } from "../create-basic-media-plan-modal";
import { MediaPlanDateRangeField } from "../media-plan-date-range-field";

export type MediaPlanBasicInfoInterface = MediaPlanTableInterface & {
  state: MMMState;
  model_config?: {
    config_name: string;
  } | null;
};
interface Props {
  currentIndex: number;
  form: UseFormReturn<MediaPlan>;
  modelConfigurations: GetBasicMediaPlans_me_mmm_basicMediaPlansData_modelConfigurations[];
  model_configuration_id: string;
  selectedMediaPlan?: MediaPlanBasicInfoInterface | null;
}

export const BasicInformationStep = ({
  currentIndex,
  form,
  modelConfigurations,
  model_configuration_id,
  selectedMediaPlan,
}: Props) => {
  const isDraft = selectedMediaPlan?.state === MMMState.draft;

  const areAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();
  const WIZARD_STEPS = areAutoblessedModelsEnabled
    ? WIZARD_STEPS_AUTOBLESSED_MODELS
    : DEPRECATED_WIZARD_STEPS;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-lg font-semibold">
          {WIZARD_STEPS[currentIndex].title}
        </div>
        {WIZARD_STEPS[currentIndex].description && isDraft && (
          <div className="text-sm text-interstellar-1">
            {WIZARD_STEPS[currentIndex].description}
          </div>
        )}
      </div>
      {isDraft || !selectedMediaPlan ? (
        <TextInput
          label="NAME"
          isRequired
          errors={form.formState.errors}
          {...form.register("name")}
        />
      ) : (
        <div className="nb-input-container">
          <label className="label mb-2">NAME</label>
          <div>{selectedMediaPlan.name || <i>No name</i>}</div>
        </div>
      )}
      {isDraft || !selectedMediaPlan ? (
        <MediaPlanDateRangeField form={form} />
      ) : (
        <div className="nb-input-container">
          <label className="label mb-2">DATE RANGE</label>
          <div>
            {`${formatDate(
              selectedMediaPlan.date_range.start_date,
            )} - ${formatDate(selectedMediaPlan.date_range.end_date)}`}
          </div>
        </div>
      )}
      {isDraft || !selectedMediaPlan ? (
        <Select<string>
          label="CONFIGURATION"
          isRequired
          className="w-80"
          options={modelConfigurations.map(({ config_name, config_id }) => ({
            value: config_id,
            label: config_name,
          }))}
          onChange={(value) => form.setValue("model_configuration_id", value)}
          value={model_configuration_id}
          isDisabled={
            areAutoblessedModelsEnabled && modelConfigurations.length === 1
          }
        />
      ) : (
        <div className="nb-input-container">
          <label className="label mb-2">SEED DATA</label>
          <div>{selectedMediaPlan.model_config?.config_name}</div>
        </div>
      )}
      {!isDraft && selectedMediaPlan && (
        <div className="nb-input-container">
          <label className="label mb-2">STATE</label>
          <div>
            {
              MMM_PRETTY_STATES[
                selectedMediaPlan.state as unknown as MMMClientState
              ]
            }
          </div>
        </div>
      )}
    </div>
  );
};
